import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import styled from 'styled-components';
import { Accounts, HoldingsConcentration } from 'actions';
import { Button, Header, Icon, Loader, Popup } from 'semantic-ui-react';
import { Segment, Button as ButtonIcon } from 'components';
import { colors } from 'utils/colors';
import cn from 'classnames';
import MultiSelect from '../../../components/Multiselect';
import { ProgressBar } from '../../../components/ProgressBar';
import { SimpleTable } from '../../../components/SimpleTable';
import RingChart from '../../../components/RingChart/RingChart';
import { ExposureChart } from '../../Portfolio/Analytics/components';
import { StockExposure } from '../../../actions';

class HoldingsConcentrationTool extends Component {
  constructor(props) {
    super(props);
    this.state = {
      selectedAccountIds: [],
      displayedAccountIds: [],
      transformedAccounts: [],
      topTen: [],
      error: false,
      accountError: '',
      fromOverview: true,
    };
    this.setSelectedAccounts = this.setSelectedAccounts.bind(this);
  }

  componentDidMount() {
    const { dispatch, holdingsConc, accountData } = this.props;
    const { fromOverview } = this.state;
    const preSelectedAccountIds = holdingsConc.preSelectedAccounts || [];
    if (preSelectedAccountIds.length > 0 && fromOverview) {
      this.setState({ fromOverview: false });
    }

    // only fetch acccounts if there is no account data in the store
    if (accountData && accountData.length > 0) {
      this.initializeData(accountData, holdingsConc, preSelectedAccountIds);
    } else {
      dispatch(Accounts.fetchAllData()).then((accountList) => {
        if (accountList) {
          this.initializeData(accountList, holdingsConc, preSelectedAccountIds);
        }
      });
    }
  }

  componentDidUpdate(prevProps) {
    const { accountData, holdingsConc } = this.props;
    const { fromOverview } = this.state;
    const preSelectedAccountIds = holdingsConc.preSelectedAccounts || [];
    if (preSelectedAccountIds.length > 0 && fromOverview) {
      this.setState({ fromOverview: false });
    }

    if (accountData && accountData !== prevProps.accountData) {
      this.handleChange(
        'transformedAccounts',
        this.transformAccountDataForMultiSelect(accountData)
      );

      // Only reset to default accounts if no preSelectedAccounts were set
      if (preSelectedAccountIds.length === 0) {
        const accountIds = accountData
          .filter((account) => account.for_aggregation)
          .map((account) => account.id);

        this.setSelectedAccounts(accountIds);

        if (accountIds.length > 0) {
          this.fetchHC(accountIds);
        }
      }
    }

    if (holdingsConc !== prevProps.holdingsConc) {
      this.handleChange('topTen', holdingsConc.holdings.slice(0, 10));
    }
  }

  setSelectedAccounts = (selectedAccountIds) => {
    this.setState({ selectedAccountIds });
    this.setState({ accountError: '' });
  };

  initializeData = (accountData, holdingsConc, preSelectedAccountIds = []) => {
    // if we already have a previous hc result, use it
    if (holdingsConc.accounts.length === 0) {
      const accountIds =
        preSelectedAccountIds.length > 0
          ? preSelectedAccountIds
          : accountData
              .filter((account) => account.for_aggregation)
              .map((account) => account.id);

      this.fetchHC(accountIds);
      this.setState({
        transformedAccounts:
          this.transformAccountDataForMultiSelect(accountData),
        selectedAccountIds: accountIds,
        accountError:
          accountIds.length === 0 ? 'Please select at least one account.' : '', // for the case where all accounts are toggled off, so no default accounts are selected
      });
    } else {
      this.setState({
        transformedAccounts:
          this.transformAccountDataForMultiSelect(accountData),
        selectedAccountIds: holdingsConc.accounts,
        displayedAccountIds: holdingsConc.accounts,
        topTen: holdingsConc.holdings.slice(0, 10),
        accountError: '',
      });
    }
  };

  fetchHC = (accountIds) => {
    const { dispatch } = this.props;
    if (accountIds.length === 0) {
      this.setState({ accountError: 'Please select at least one account.' });
    } else {
      dispatch(HoldingsConcentration.fetch(accountIds)).then((result) => {
        if (result.length === 0) {
          this.setState({
            error: true,
          });
        } else {
          this.setState({ error: false, displayedAccountIds: accountIds });
        }
      });
    }
  };

  handleNewSelection = (newAccountIds) => {
    this.fetchHC(newAccountIds);
  };

  handleChange = (name, value) => this.setState({ [name]: value });

  generatePDF = () => {
    const { history } = this.props;

    return history.push('/tools/holdings-concentration/pdf');
  };

  transformAccountDataForMultiSelect = (accountData) =>
    accountData.map((account) => {
      const details = [account.user_institution, account.label];
      const joined = details.join(': ');
      return {
        id: account.id,
        name: joined,
      };
    });

  handleExposureSelect = (ticker) => {
    const { dispatch, history } = this.props;
    const { selectedAccountIds } = this.state;

    dispatch(StockExposure.clear());
    dispatch(StockExposure.fetch([ticker], selectedAccountIds));
    dispatch(StockExposure.setSelectedAccounts(selectedAccountIds));
    dispatch(StockExposure.addPrevSearch(ticker));
    history.push('/tools/stock-exposure');
  };

  renderAccountChips = () => {
    const { displayedAccountIds, transformedAccounts } = this.state;
    const { accountData } = this.props;

    if (!accountData) return null;

    const aggregationIds = accountData
      .filter((a) => a.for_aggregation)
      .map((a) => a.id);

    const isAllAccounts =
      aggregationIds.length > 0 &&
      aggregationIds.length === displayedAccountIds.length &&
      aggregationIds.every((id) => displayedAccountIds.includes(id));

    if (isAllAccounts) return null;

    const selectedLabels = displayedAccountIds
      .map((id) => {
        const match = transformedAccounts.find((a) => a.id === id);
        return match ? match.name : null;
      })
      .filter(Boolean);

    return selectedLabels;
  };

  render() {
    const {
      selectedAccountIds,
      transformedAccounts,
      topTen,
      error,
      accountError,
      fromOverview,
    } = this.state;
    const { isFetchingHC, isFetchingAcc, accountData, isPrinting } = this.props;
    let tickers = [];
    let weights = [];
    let topTenWeight = 0;
    let top = 0;
    let top3 = 0;
    let top5 = 0;
    if (topTen.length !== 0) {
      top = topTen[0].weight;
      tickers = topTen.map((holding) => holding.ticker);
      tickers.push('Remainder');

      weights = topTen.map((holding, i) => {
        if (i < 3) {
          top3 += holding.weight;
        }
        if (i < 5) {
          top5 += holding.weight;
        }
        topTenWeight += holding.weight;
        return holding.weight;
      });
      weights.push(1 - topTenWeight);
    }
    const accountsAreEmpty =
      accountData && Object.keys(accountData).length === 0;

    return (
      <Segment
        margin={!isPrinting ? '1rem' : '0rem 3rem 2rem 3rem'}
        textAlign="center"
        style={{
          position: 'relative',
          minHeight: '700px',
          padding: !isPrinting ? '2rem 3rem' : '0rem 3rem',
        }}
      >
        <Header size="large" textAlign="left" style={{ margin: '0 0  1rem 0' }}>
          Stock Holdings Concentration Analysis
        </Header>
        {!isPrinting && (
          <ButtonIcon
            className={cn(
              'left',
              isFetchingHC || isFetchingAcc ? 'disabled' : ''
            )}
            icon="file text"
            color={colors.darkGrey}
            onClick={this.generatePDF}
            style={{
              position: 'absolute',
              top: '1.5rem',
              right: '3.3rem',
            }}
          >
            Print/PDF
          </ButtonIcon>
        )}
        <ContentContainer isPrinting={isPrinting}>
          <div className="row">
            <Header
              size="large"
              textAlign="left"
              style={{ margin: 0, width: !isPrinting ? '60%' : '80%' }}
            >
              <Header.Subheader>
                This tool helps you understand the concentration of the selected
                portfolio(s). Below are the top 10 stocks by market value.
              </Header.Subheader>
              {(() => {
                const chips = this.renderAccountChips();
                const MAX_CHIPS = 3;
                return (
                  <AccountChipsRow>
                    <span className="showing-label">
                      <Icon
                        name="book"
                        style={{
                          fontSize: '15px',
                          verticalAlign: 'initial',
                          color: 'black',
                          marginRight: 0,
                        }}
                      />
                      Showing:
                    </span>
                    {chips === null ? (
                      <span className="chip">All accounts</span>
                    ) : chips.length === 0 ? null : (
                      <React.Fragment>
                        {chips
                          .slice(0, chips.length > 4 ? MAX_CHIPS : chips.length)
                          .map((label) => (
                            <span key={label} className="chip">
                              {label}
                            </span>
                          ))}
                        {chips.length > 4 && (
                          <span className="chip-overflow">
                            +{chips.length - MAX_CHIPS} more
                          </span>
                        )}{' '}
                      </React.Fragment>
                    )}
                  </AccountChipsRow>
                );
              })()}
            </Header>
            {!isPrinting && fromOverview && (
              <div className="accounts">
                <div className="accounts-select">
                  <MultiSelect
                    items={transformedAccounts}
                    selectedItems={selectedAccountIds}
                    setSelectedItems={this.setSelectedAccounts}
                    placeholder="Select account(s)"
                    maxHeight="300px"
                  />
                  <Popup
                    trigger={
                      <Button
                        icon
                        onClick={() =>
                          this.handleNewSelection(selectedAccountIds)
                        }
                        style={{
                          background: colors.periwinkle,
                          color: 'white',
                        }}
                      >
                        <Icon name="refresh" />
                      </Button>
                    }
                    content="Recalculate"
                    position="top center"
                  />
                </div>
                {accountError.length > 0 && !accountsAreEmpty && (
                  <div style={{ fontSize: '15px', marginTop: '5px' }}>
                    <Icon name="warning sign" style={{ color: colors.red }} />
                    <span style={{ color: colors.red }}>{accountError} </span>
                  </div>
                )}
              </div>
            )}
          </div>
          {!isFetchingHC && !isFetchingAcc ? (
            <div>
              {accountsAreEmpty && (
                <WarningMessage>
                  <Icon name="warning sign" /> This tool requires you to have
                  saved accounts in Wealthscope. <br /> Please Upload, Manually
                  Enter or Link your Accounts to get started.
                </WarningMessage>
              )}
              {error && (
                <WarningMessage>
                  No holdings data is available for the selected account(s).
                  Please verify the account selection or contact support for
                  assistance.
                </WarningMessage>
              )}
              {topTen.length > 0 && (
                <div className="row" style={{ alignItems: 'stretch' }}>
                  <ElementWrapper className="ringchart">
                    <Header
                      size="medium"
                      style={{ margin: 0, textAlign: 'left', width: '100%' }}
                    >
                      Top 10 Holdings
                    </Header>
                    <div className="ringchart-container-parent">
                      <div className="ringchart-container">
                        {!isPrinting ? (
                          <RingChart
                            labels={tickers}
                            values={weights}
                            selected={0}
                          />
                        ) : (
                          <ExposureChart
                            title=""
                            labels={tickers}
                            data={weights}
                            showTitle={false}
                          />
                        )}
                      </div>
                    </div>
                  </ElementWrapper>
                  <div className="insights">
                    <ElementWrapper>
                      <Header
                        size="medium"
                        style={{ margin: 0, width: '100%' }}
                      >
                        Key Insights
                        <div className="progress-header">
                          <Header.Subheader className="subheader">
                            Top Holding
                          </Header.Subheader>
                          <Header.Subheader className="stat">
                            {Math.round(top * 10000) / 100}%
                          </Header.Subheader>
                        </div>
                        <ProgressBar currentValue={top} />
                        <div className="progress-header">
                          <Header.Subheader className="subheader">
                            Top 3 Holdings
                          </Header.Subheader>
                          <Header.Subheader className="stat">
                            {Math.round(top3 * 10000) / 100}%
                          </Header.Subheader>
                        </div>
                        <ProgressBar currentValue={top3} />
                        <div className="progress-header">
                          <Header.Subheader className="subheader">
                            Top 5 Holdings
                          </Header.Subheader>
                          <Header.Subheader className="stat">
                            {Math.round(top5 * 10000) / 100}%
                          </Header.Subheader>
                        </div>
                        <ProgressBar currentValue={top5} />
                        <div className="progress-header">
                          <Header.Subheader className="subheader">
                            Top 10 Holdings
                          </Header.Subheader>
                          <Header.Subheader className="stat">
                            {Math.round(topTenWeight * 10000) / 100}%
                          </Header.Subheader>
                        </div>
                        <ProgressBar currentValue={topTenWeight} />
                      </Header>
                      {!isPrinting && (
                        <Header>
                          <Header.Subheader>
                            <b style={{ color: 'black' }}>Note:</b> To see where
                            each holding is held, click the "magnifying glass"
                            icon on the right in the table below. It will take
                            you to the Stock Exposure LookUp tool.
                          </Header.Subheader>
                        </Header>
                      )}
                    </ElementWrapper>
                  </div>
                </div>
              )}
            </div>
          ) : (
            <Loader active />
          )}
        </ContentContainer>
        {!isFetchingAcc &&
          !isFetchingHC &&
          !accountsAreEmpty &&
          topTen.length !== 0 &&
          !error && (
            <div>
              <ElementWrapper>
                <Header size="medium">Holdings Details</Header>
                <SimpleTable>
                  <thead>
                    <tr>
                      <th className="left">Top 10 Holdings</th>
                      <th># of Accounts</th>
                      <th>Sector</th>
                      <th>Country</th>
                      <th className="left">Concentration Breakdown</th>
                      <th />
                    </tr>
                  </thead>
                  <tbody>
                    {topTen.map((holding) => (
                      <tr key={holding.ticker}>
                        <td className="holdings-cell">
                          <div className="ticker">{holding.ticker}</div>
                          <div>{holding.name}</div>
                        </td>
                        <td>{Object.keys(holding.in_accounts).length}</td>
                        <td>{holding.sector || '-'}</td>
                        <td>{holding.country || '-'}</td>
                        <td>
                          <div className="concentration-cell">
                            <ProgressBar
                              currentValue={holding.weight}
                              className="progress"
                            />
                            <div className="progress">
                              {Math.round(holding.weight * 10000) / 100}%
                            </div>
                          </div>
                        </td>
                        <td>
                          {!isPrinting && (
                            <Popup
                              trigger={
                                <Button
                                  icon
                                  basic
                                  onClick={() =>
                                    this.handleExposureSelect(holding.ticker)
                                  }
                                >
                                  <Icon name="search" />
                                </Button>
                              }
                              content="See where you hold this stock in Stock Exposure Lookup."
                              position="top center"
                            />
                          )}
                        </td>
                      </tr>
                    ))}
                    <tr>
                      <td />
                    </tr>
                  </tbody>
                </SimpleTable>
              </ElementWrapper>
              {!isPrinting ? (
                <Popup
                  trigger={<LinkButton>Methodology</LinkButton>}
                  wide
                  position="top center"
                  style={{ maxWidth: '650px' }}
                >
                  For funds, the calculations are based on their 25 top
                  holdings. Note that a fund's holdings and weights may have
                  changed since its last quarterly SEC/SEDAR filing. Further due
                  diligence is recommended.
                </Popup>
              ) : (
                <div
                  style={{
                    margin: 'auto',
                    marginTop: '1rem',
                    maxWidth: '60%',
                  }}
                >
                  For funds, the calculations are based on their 25 top
                  holdings. Note that a fund's holdings and weights may have
                  changed since its last quarterly SEC/SEDAR filing. Further due
                  diligence is recommended.
                </div>
              )}
            </div>
          )}
      </Segment>
    );
  }
}

HoldingsConcentrationTool.propTypes = {
  dispatch: PropTypes.func.isRequired,
  accountData: PropTypes.array,
  holdingsConc: PropTypes.object,
  isFetchingHC: PropTypes.bool,
  isFetchingAcc: PropTypes.bool,
  history: PropTypes.object,
  isPrinting: PropTypes.bool,
};

HoldingsConcentrationTool.defaultProps = {
  accountData: [],
  holdingsConc: {},
  isFetchingHC: false,
  isFetchingAcc: false,
  history: {},
  isPrinting: false,
};

export default connect((state) => ({
  user: state.Auth.user,
  accountData: state.Accounts.accountData,
  holdingsConc: state.HoldingsConcentration,
  isFetchingHC: state.HoldingsConcentration.isFetching,
  isFetchingAcc: state.Accounts.isFetching,
}))(HoldingsConcentrationTool);

const ContentContainer = styled.div`
  display: flex;
  flex-direction: column;
  row-gap: ${(props) => (!props.isPrinting ? '2rem' : '1rem')};
  margin-bottom: ${(props) => (!props.isPrinting ? '2rem' : '1rem')};

  .row {
    display: flex;
    flex-direction: row;
    text-align: left;
    align-items: start;
    column-gap: 2rem;

    .accounts {
      display: flex;
      flex-direction: column;
      width: 40%;
      align-self: end;

      .accounts-select {
        display: flex;
        flex-direction: row;
        column-gap: 5px;
      }
    }

    .insights {
      display: flex;
      width: 40%;
    }
  }
`;

const ElementWrapper = styled.div`
  border: 1px solid #ccc;
  border-radius: 8px;
  padding: 2rem;
  text-align: left;
  width: 100%;

  &.ringchart {
    width: 60%;
    display: flex;
    flex-direction: column;
  }
  .ringchart-container {
    max-height: 400px;
    max-width: 400px;
    display: block;
    margin-left: auto;
    margin-right: auto;
  }
  .ringchart-container-parent {
    height: 100%;
    width: 100%;
  }

  .left {
    text-align: left;
  }
  .subheader {
    font-weight: bold !important;
  }

  .stat {
    font-weight: bold !important;
    color: ${colors.navyBlue} !important;
  }

  .progress-header {
    display: flex;
    flex-direction: row;
    justify-content: space-between;
    margin-top: 1rem;
    width: 100%;
  }

  .concentration-cell {
    .progress {
      width: 30%;
    }
    display: flex;
    flex-direction: row;
    column-gap: 10px;
    align-items: center;
    height: 100%;
  }

  .holdings-cell {
    display: flex;
    flex-direction: column;
    text-align: left;

    .ticker {
      font-weight: bold;
      color: ${colors.darkGrey};
    }

    .total {
      font-weight: bold;
      color: ${colors.navyBlue};
    }
  }

  .insight-text {
    margin-top: 2rem;
  }
`;

const WarningMessage = styled.div`
  color: ${colors.red};
  font-weight: bold;
  margin-top: 2rem;
`;

const LinkButton = styled.button`
  border: none;
  color: ${colors.blue};
  background: white;
  font-weight: bold;
  margin-top: 1rem;

  &: hover {
    text-decoration: underline;
  }
`;

const AccountChipsRow = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  flex-wrap: wrap;
  gap: 6px;
  margin-top: 0.5rem;
  font-size: 14px;
  color: ${colors.darkGrey};

  .showing-label {
    display: flex;
    gap: 6px;
    color: ${colors.darkGrey};
  }

  .chip {
    background: #f0f2f5;
    border-radius: 12px;
    padding: 2px 10px;
    font-size: 14px;
    color: #333;
    white-space: nowrap;
  }

  .chip-overflow {
    background: #dce4f5;
    color: ${colors.navyBlue};
    border-radius: 12px;
    padding: 2px 10px;
    font-size: 14px;
    white-space: nowrap;
  }
`;
